<template>
  <div class="user-management">
    <el-card shadow="never" class="animate__animated animate__fadeInUp">
      <div class="filter-card">
        <div style="display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:10px;">
          <div style="display:flex;flex-wrap:wrap;align-items:center;gap:10px;">
            <el-input
              v-model="searchUsername"
              placeholder="搜索用户名"
              prefix-icon="el-icon-search"
              clearable
              @keyup.enter.native="handleSearch"
              style="width:160px;"
            ></el-input>
            <el-input
              v-model="searchInviteCode"
              placeholder="搜索邀请码"
              prefix-icon="el-icon-collection-tag"
              clearable
              @keyup.enter.native="handleSearch"
              style="width:160px;"
            ></el-input>
            <el-button
              type="primary"
              icon="el-icon-search"
              @click="handleSearch"
            >搜索</el-button>
          </div>
          <div style="display:flex;flex-wrap:wrap;align-items:center;gap:180px;">
            <el-tag type="warning" effect="plain" size="medium">
              <i class="el-icon-coin"></i> 累计金币：{{ totalCoins }}
            </el-tag>
            <el-button
              type="primary"
              icon="el-icon-refresh"
              @click="refreshList"
            >刷新列表</el-button>
          </div>
        </div>
      </div>

      <div v-if="error" class="error">{{ error }}</div>
      <el-empty
        v-else-if="!loading && users.length === 0"
        description="暂无用户数据"
      />

      <div v-else>
        <!-- 用户表格 -->
        <el-table
          :data="users"
          border
          stripe
          v-loading="loading"
          element-loading-text="正在加载用户数据..."
          class="animate__animated animate__fadeIn"
        >
          <el-table-column prop="id" label="ID" width="80" align="center" />
          <el-table-column prop="username" label="用户名" min-width="150" show-overflow-tooltip />
          <el-table-column prop="parentId" label="上级用户ID" width="120" align="center">
            <template slot-scope="scope">
              {{ scope.row.parentId || '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="inviteCode" label="邀请码" width="150" align="center">
            <template slot-scope="scope">
              {{ scope.row.inviteCode || '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="status" label="账号状态" width="100" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.status === 0" type="success" size="small">正常</el-tag>
              <el-tag v-else-if="scope.row.status === 1" type="danger" size="small">封禁</el-tag>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="isCaptain" label="是否为团长" width="110" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.isCaptain === 1" type="success" size="small">是</el-tag>
              <el-tag v-else-if="scope.row.isCaptain === 0" type="info" size="small">否</el-tag>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="createdAt" label="创建时间" width="180" align="center">
            <template slot-scope="scope">
              {{ formatDate(scope.row.createdAt) }}
            </template>
          </el-table-column>
          <el-table-column prop="updatedAt" label="更新时间" width="180" align="center">
            <template slot-scope="scope">
              {{ formatDate(scope.row.updatedAt) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="420" align="center">
            <template slot-scope="scope">
              <div style="display: flex; justify-content: center; align-items: center; gap: 8px;">
                <el-button
                  size="mini"
                  type="primary"
                  icon="el-icon-wallet"
                  @click="handleCheckBalance(scope.row)"
                >查询余额</el-button>
                <el-button
                  size="mini"
                  type="info"
                  icon="el-icon-tickets"
                  @click="handleViewFlow(scope.row)"
                >查看流水</el-button>
                <el-button
                  size="mini"
                  :type="scope.row.status === 0 ? 'danger' : 'success'"
                  :icon="scope.row.status === 0 ? 'el-icon-close' : 'el-icon-check'"
                  @click="handleToggleStatus(scope.row)"
                >{{ scope.row.status === 0 ? '禁用' : '启用' }}</el-button>
                <el-button
                  size="mini"
                  type="success"
                  icon="el-icon-edit"
                  @click="handleEditUser(scope.row)"
                >编辑</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="pagination.pageNum"
            :page-size="pagination.pageSize"
            :page-sizes="[5, 10, 20, 50]"
            :total="pagination.total"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 新增/编辑用户对话框 -->
    <el-dialog
      :title="'编辑用户'"
      :visible.sync="addDialogVisible"
      width="500px"
      center
    >
      <el-form :model="currentUser" :rules="userRules" ref="userForm" label-width="120px">
        <el-form-item label="用户名" prop="username">
          <el-input v-model="currentUser.username" :placeholder="isEdit ? '请输入用户名' : '请输入用户名'"></el-input>
        </el-form-item>
        <el-form-item label="邀请码" prop="inviteCode">
          <el-input v-model="currentUser.inviteCode" placeholder="请输入邀请码（可选）"></el-input>
        </el-form-item>
        <el-form-item label="上级用户 ID" prop="parentId">
          <el-input v-model="currentUser.parentId" type="number" placeholder="请输入上级用户 ID"></el-input>
        </el-form-item>
        <el-form-item label="账号状态" prop="status">
          <el-radio-group v-model="currentUser.status">
            <el-radio :label="0">正常</el-radio>s
            <el-radio :label="1">封禁</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="是否为团长" prop="isCaptain">
          <el-radio-group v-model="currentUser.isCaptain">
            <el-radio :label="1">是</el-radio>
            <el-radio :label="0">否</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="addDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="confirmAddUser" :loading="submitLoading">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 查询余额对话框 -->
    <el-dialog
      title="用户余额详情"
      :visible.sync="balanceDialogVisible"
      width="500px"
      center
    >
      <div v-loading="balanceLoading">
        <el-form label-width="120px" :model="userBalance" class="balance-form">
          <el-form-item label="用户 ID:">
            <span>{{ userBalance.id || '-' }}</span>
          </el-form-item>
          <el-form-item label="用户名:">
            <span>{{ userBalance.username || '-' }}</span>
          </el-form-item>
          <el-form-item label="当前金币余额:">
            <span>{{ userBalance.coinBalance || 0 }}</span>
          </el-form-item>
          <el-form-item label="累计获得金币:">
            <span>{{ userBalance.totalEarned || 0 }}</span>
          </el-form-item>
          <el-form-item label="累计提现扣除:">
            <span>{{ userBalance.totalWithdrawn || 0 }}</span>
          </el-form-item>
          <el-form-item label="更新时间:">
            <span>{{ formatDate(userBalance.updatedAt) }}</span>
          </el-form-item>
        </el-form>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="balanceDialogVisible = false">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { request } from '@/utils/http'

export default {
  name: 'UserManagement',
  data() {
    return {
      users: [],
      loading: false,
      error: '',
      searchUsername: '',
      searchInviteCode: '',
      totalCoins: 0,
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      addDialogVisible: false,
      isEdit: false,
      currentUser: {},
      submitLoading: false,
      userRules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 3, max: 20, message: '长度在 3 到 20 个字符', trigger: 'blur' }
        ]
      },
      balanceDialogVisible: false,
      balanceLoading: false,
      userBalance: {}
    }
  },
  async mounted() {
    await this.fetchUsers()
  },
  methods: {
    async fetchUsers() {
      this.loading = true
      this.error = ''
      
      try {
        // 构建查询参数
        let params = new URLSearchParams();
        params.append('pageNum', this.pagination.pageNum);
        params.append('pageSize', this.pagination.pageSize);
        
        // 添加用户名筛选参数
        if (this.searchUsername) {
          params.append('username', this.searchUsername);
        }

        // 添加邀请码筛选参数
        if (this.searchInviteCode) {
          params.append('inviteCode', this.searchInviteCode);
        }
        
        const queryString = params.toString();
        const response = await request(`/admin/users/page?${queryString}`)
        this.users = response.data.records
        this.pagination.total = response.data.total
        this.totalCoins = response.data.totalCoins || 0
        
      } catch (error) {
        console.error('获取用户列表失败:', error)
        this.error = error.message || '获取用户列表失败'
      } finally {
        this.loading = false
      }
    },
    
    handleCurrentChange(pageNum) {
      this.pagination.pageNum = pageNum
      this.fetchUsers()
    },
    
    handleSizeChange(pageSize) {
      this.pagination.pageSize = pageSize
      this.pagination.pageNum = 1
      this.fetchUsers()
    },
    
    handleSearch() {
      this.pagination.pageNum = 1
      this.fetchUsers()
    },
    
    refreshList() {
      this.searchUsername = ''
      this.searchInviteCode = ''
      this.pagination.pageNum = 1
      this.fetchUsers()
    },
    
    handleEditUser(user) {
      this.isEdit = true
      this.currentUser = {
        id: user.id,
        username: user.username,
        inviteCode: user.inviteCode || '',
        parentId: user.parentId,
        status: user.status,
        isCaptain: user.isCaptain !== undefined ? user.isCaptain : 0,
        commissionRate: user.commissionRate || '10'
      }
      this.addDialogVisible = true
      this.$nextTick(() => {
        this.$refs.userForm && this.$refs.userForm.clearValidate()
      })
    },
    
    async confirmAddUser() {
      if (!this.$refs.userForm) return
      
      await this.$refs.userForm.validate(async (valid) => {
        if (!valid) return
        
        this.submitLoading = true
        try {
          let response
          if (this.isEdit) {
            response = await request('/admin/users/update', {
              method: 'PUT',
              body: JSON.stringify({
                id: this.currentUser.id,
                username: this.currentUser.username,
                inviteCode: this.currentUser.inviteCode,
                parentId: this.currentUser.parentId,
                status: this.currentUser.status,
                isCaptain: this.currentUser.isCaptain,
                commissionRate: this.currentUser.commissionRate
              })
            })
          } else {
            response = await request('/admin/users/add', {
              method: 'POST',
              body: JSON.stringify({
                username: this.currentUser.username,
                inviteCode: this.currentUser.inviteCode,
                parentId: this.currentUser.parentId,
                status: this.currentUser.status,
                isCaptain: this.currentUser.isCaptain,
                commissionRate: this.currentUser.commissionRate
              })
            })
          }
          
          if (response && response.code === 200) {
            this.$message.success(response.msg || '修改成功')
            this.addDialogVisible = false
            this.fetchUsers()
          } else {
            throw new Error(response.msg || '修改失败')
          }
        } catch (error) {
          this.$message.error(error.message || '修改失败')
        } finally {
          this.submitLoading = false
        }
      })
    },
    
    async handleToggleStatus(user) {
      const actionText = user.status === 0 ? '禁用' : '启用'
      
      try {
        const response = await request(`/admin/users/${user.id}/status?status=${user.status}`, {
          method: 'PUT'
        })
        
        if (response && response.code === 200) {
          this.$message.success(response.msg || `${actionText}成功`)
          user.status = user.status === 0 ? 1 : 0
        } else {
          throw new Error(response.msg || `${actionText}失败`)
        }
      } catch (error) {
        this.$message.error(error.message || `${actionText}失败`)
      }
    },
    
    async handleCheckBalance(user) {
      this.balanceDialogVisible = true
      this.balanceLoading = true
      this.userBalance = {}
      try {
        const response = await request(`/admin/user-balance/get-balance?username=${encodeURIComponent(user.username)}`)
        if (response && response.code === 200) {
          this.userBalance = response.data
        } else {
          this.$message.error(response.msg || '获取余额失败')
        }
      } catch (error) {
        console.error('获取余额失败:', error)
        this.$message.error(error.message || '获取余额失败')
      } finally {
        this.balanceLoading = false
      }
    },
    
    handleViewFlow(user) {
      this.$router.push({
        path: '/coin-flow',
        query: { username: user.username }
      })
    },
    

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.user-management {
}

.filter-card {
  margin-bottom: 20px;
}

.error {
  color: #f87171;
  padding: 10px;
  background-color: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 4px;
  margin-bottom: 20px;
}

.el-table {
  margin-bottom: 20px;
}

.pagination-container {
  display: flex;
  justify-content: center;
}

.dialog-footer {
  text-align: right;
}

.balance-form .el-form-item {
  margin-bottom: 15px;
  border-bottom: 1px dashed #ebeef5;
  padding-bottom: 5px;
}

.balance-form .el-form-item:last-child {
  border-bottom: none;
}
</style>
